import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Check, X, Pencil, Loader2, CheckCircle2, XCircle, AlertTriangle, Trash2, Zap, Wand2, KeyRound } from "lucide-react";

const RISK = { High: "#E5325F", Medium: "#FF8A3D", Low: "#0F9D77" };
const STATUS = {
  draft: { label: "Awaiting review", color: "#7C3AED" },
  approved: { label: "Approved", color: "#0F9D77" },
  rejected: { label: "Rejected", color: "#E5325F" },
};
const LAST = { passed: [CheckCircle2, "#0F9D77"], failed: [XCircle, "#E5325F"], blocked: [AlertTriangle, "#FF8A3D"] };

export default function ScriptCard({ s, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState("");
  const [draft, setDraft] = useState(null);
  const [showPrev, setShowPrev] = useState(false);
  const st = STATUS[s.status || "draft"];
  const Last = s.last_status ? LAST[s.last_status] : null;
  const LastIcon = Last ? Last[0] : null;

  const act = async (patch, key) => {
    setBusy(key);
    try { await onUpdate(s.id, patch); } finally { setBusy(""); }
  };

  const openEdit = () => {
    setDraft({ name: s.name, module: s.module, priority: s.priority, steps: s.steps.join("\n"), expected_result: s.expected_result });
    setEditing(true);
  };

  const saveEdit = async () => {
    await act({ ...draft, steps: draft.steps.split("\n") }, "edit");
    setEditing(false);
  };

  return (
    <div className={`card-soft p-4 ${s.status === "rejected" ? "opacity-60" : ""}`} data-testid={`script-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-[#1E1560]">{s.name}</span>
            {LastIcon && <LastIcon className="w-4 h-4" style={{ color: Last[1] }} title={`Last run: ${s.last_status}`} />}
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <Badge className="rounded-full text-[10px]" style={{ background: `${st.color}1F`, color: st.color }} data-testid={`script-status-${s.id}`}>{st.label}</Badge>
            <Badge className="rounded-full bg-muted text-secondary text-[10px]">{s.module}</Badge>
            <Badge className="rounded-full text-[10px]" style={{ background: `${RISK[s.priority] || "#3A2DB8"}1F`, color: RISK[s.priority] || "#3A2DB8" }}>{s.priority}</Badge>
            {s.actions?.length > 0 && (
              <Badge className="rounded-full text-[10px] bg-[#2A1E9E]/10 text-[#2A1E9E] gap-1" data-testid={`script-scripted-${s.id}`} title="Explicit browser actions — no AI guesswork at run time">
                <KeyRound className="w-3 h-3" /> Scripted
              </Badge>
            )}
            {s.impact_flag && (
              <Badge className="rounded-full text-[10px] bg-[#FF6B3D]/15 text-[#F4511E] gap-1" data-testid={`script-impacted-${s.id}`} title={s.impact_flag.change_summary}>
                <Zap className="w-3 h-3" /> Release impacted · {s.impact_flag.risk}
              </Badge>
            )}
            {s.needs_reapproval && (
              <Badge className="rounded-full text-[10px] bg-[#7C3AED]/15 text-[#7C3AED] gap-1" data-testid={`script-healed-${s.id}`}>
                <Wand2 className="w-3 h-3" /> Rebuilt — re-approve
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {s.status !== "approved" && (
            <Button size="sm" onClick={() => act({ status: "approved" }, "approve")} disabled={!!busy} data-testid={`script-approve-${s.id}`}
              className="rounded-full h-8 bg-[#0F9D77] hover:bg-[#0B7F60] text-white gap-1 px-3">
              {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
            </Button>
          )}
          {s.status !== "rejected" && (
            <Button size="sm" variant="ghost" onClick={() => act({ status: "rejected" }, "reject")} disabled={!!busy} data-testid={`script-reject-${s.id}`}
              className="rounded-full h-8 text-[#E5325F] hover:bg-[#E5325F]/10 gap-1 px-3">
              <X className="w-3 h-3" /> Reject
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={openEdit} data-testid={`script-edit-${s.id}`} className="rounded-full h-8 w-8 p-0 text-[#3A2DB8] hover:bg-[#3A2DB8]/10">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)} data-testid={`script-delete-${s.id}`} className="rounded-full h-8 w-8 p-0 text-secondary hover:text-[#E5325F]">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <ol className="mt-3 space-y-1 text-xs text-secondary list-decimal list-inside">
        {s.steps.map((step, i) => <li key={`${i}-${step.slice(0, 24)}`}>{step}</li>)}
      </ol>
      <p className="mt-2 text-xs text-[#1E1560]"><span className="text-secondary">Expected: </span>{s.expected_result}</p>
      {s.data_summary && Object.keys(s.data_summary).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`script-data-${s.id}`}>
          {Object.entries(s.data_summary).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 rounded-md bg-[#F1EDFB] border border-border px-2 py-0.5 text-[10px] font-mono text-[#2A1E9E]">
              <span className="text-secondary">{k}=</span>{v}
            </span>
          ))}
          {s.note && <span className="text-[10px] text-[#B45309] self-center">{s.note}</span>}
        </div>
      )}
      {s.heal_note && (
        <div className="mt-3 rounded-lg bg-[#7C3AED]/5 border border-[#7C3AED]/20 p-3 text-xs" data-testid={`script-heal-note-${s.id}`}>
          <p className="text-[#1E1560]"><span className="font-semibold text-[#7C3AED]">Agent rebuild:</span> {s.heal_note}</p>
          {s.previous_version && (
            <button onClick={() => setShowPrev(!showPrev)} className="mt-1 text-[#3A2DB8] hover:text-[#FF6B3D]" data-testid={`script-prev-toggle-${s.id}`}>
              {showPrev ? "Hide" : "Show"} previous version
            </button>
          )}
          {showPrev && s.previous_version && (
            <ol className="mt-2 space-y-0.5 text-secondary list-decimal list-inside line-through">
              {s.previous_version.steps?.map((p, i) => <li key={`${i}-${p.slice(0, 24)}`}>{p}</li>)}
            </ol>
          )}
        </div>
      )}
      {s.last_error && s.last_status === "failed" && <p className="mt-2 text-[10px] font-mono text-[#E5325F] break-words">Last failure: {s.last_error.slice(0, 160)}</p>}
      {s.approved_by && <p className="mt-2 text-[10px] text-secondary font-mono">Approved by {s.approved_by} · {s.approved_at?.slice(0, 16).replace("T", " ")}</p>}

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="rounded-xl max-w-lg" data-testid="script-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[#1E1560]">Edit test script</DialogTitle><DialogDescription className="text-xs text-secondary">Editing steps switches this script back to AI-planned execution and requires re-approval.</DialogDescription></DialogHeader>
          {draft && (
            <div className="space-y-3 mt-2">
              <div><Label className="text-xs text-secondary">Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="script-edit-name" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-secondary">Module</Label>
                  <Input value={draft.module} onChange={(e) => setDraft({ ...draft, module: e.target.value })} data-testid="script-edit-module" className="mt-1" /></div>
                <div><Label className="text-xs text-secondary">Priority</Label>
                  <Input value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} data-testid="script-edit-priority" className="mt-1" placeholder="High | Medium | Low" /></div>
              </div>
              <div><Label className="text-xs text-secondary">Steps (one per line)</Label>
                <Textarea rows={6} value={draft.steps} onChange={(e) => setDraft({ ...draft, steps: e.target.value })} data-testid="script-edit-steps" className="mt-1 text-xs font-mono" /></div>
              <div><Label className="text-xs text-secondary">Expected result</Label>
                <Textarea rows={2} value={draft.expected_result} onChange={(e) => setDraft({ ...draft, expected_result: e.target.value })} data-testid="script-edit-expected" className="mt-1 text-xs" /></div>
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button onClick={saveEdit} disabled={busy === "edit"} data-testid="script-edit-save" className="rounded-full bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white w-full">
              {busy === "edit" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
